import PropTypes from 'prop-types'
import { productPropTypes } from 'Types'
import React, { Fragment } from 'react'
import get from 'lodash/get'
import { withRouter } from 'react-router'
import { ProductPrice, LazyLoadImage, Link, FavouriteButtonIcon } from 'Blocks'
import {
  path,
  cutTitleToLimit,
  useProcessAffiliateLink,
  isClient,
  gaReady,
  gtagReady,
} from 'Utils'
import config, { gaSelectors } from 'Config'
import { PAGE_CONTEXT as p, SKIP_IMAGEKIT_STORES } from 'Constants'

const ProductCard = props => {
  const {
    product,
    product: {
      uuid,
      image,
      title,
      slug,
      store,
      prices,
      flags: { isDiscounted, isStocked }
    },
    useCompareModal = false,
    handleOpen = () => {},
    showStore = false,
    ourAffiliateLink,
    gaEventContext = '',
    position = '',
    className = '',
    style = {},
    pageName,
    match: { params }
  } = props

  function sendGAEvent(event) {
    if (gaReady()) {
      // eslint-disable-next-line no-undef
      ga('send', event)
      // eslint-disable-next-line no-undef
      ga(`${process.env.ANT_GA_CLIENT_TRACKER_NAME}.send`, event)
      // eslint-disable-next-line no-undef
    }

  }

  function sendGtagEvent(event, additionalProps) {
    if (gtagReady) {
      // eslint-disable-next-line no-undef
      gtag(
        'event',
        event.eventAction,
        {
          category: event.eventCategory,
          tracker: `${process.env.ANT_GA_CLIENT_TRACKER_NAME}.send`,
          ...additionalProps,
        }
      )
    }
  }

  let {
    product: { affiliateUrl }
  } = props
  affiliateUrl = useProcessAffiliateLink(affiliateUrl)

  const isComparePage = pageName === p.COMPARE
  const isComparandProduct = isComparePage
    ? uuid === get(params, 'uuid', '')
    : false
  const comparandClass = isComparePage && isComparandProduct ? 'comparand' : ''
  const isSearch = props.match.params.subCategory === 'search'

  const shortTitle = cutTitleToLimit(title)
  const productPagelink = path('product', { uuid, slug })

  const GAlabel = [
    uuid,
    `store=${store.name}`,
    `productPosition=${position}`,
    `isStocked=${isStocked}`,
    `isDiscounted=${isDiscounted}`,
    `price=${prices.raw}`
  ]

  const additionalProps = {
    uuid,
    store: store.name,
    productCardStore: store.name,
    position,
    isStocked,
    isDiscounted,
    price: prices.raw, // currency ?
  }

  const textSettings = gaSelectors.getProductCardSettings.text

  const gaEvent = {
    hitType: 'event',
    eventCategory: textSettings.category.replace(
      '$1',
      isSearch ? 'Search' : gaEventContext
    ),
    eventAction: textSettings.action,
    eventLabel: GAlabel.join(' - ')
  }

  /* send to interstitial page */
  const outgoingLink = config.interStitialEnabled
    ? path(p.OUTGOING, {
        storeName: store.name,
        to: encodeURIComponent(affiliateUrl)
      })
    : affiliateUrl

  const renderContent = () => (
    <Fragment>
      <div className="image-link">
        <LazyLoadImage
          alt={shortTitle}
          image={image}
          useOriginal={SKIP_IMAGEKIT_STORES.includes(store.name.toLowerCase())}
        />
      </div>
      <div key="footer" className="footer">
        {showStore && <h4 className="product-brand">{store.name}</h4>}
        <h4>{shortTitle}</h4>
        <ProductPrice {...{ prices, isDiscounted, isStocked }} />
      </div>
    </Fragment>
  )

  const handleGA = () => {
    sendGAEvent(gaEvent)
    sendGtagEvent(gaEvent, additionalProps)
  }

  if (!isStocked && useCompareModal) {
    return (
      <div
        onClick={handleOpen}
        className={`product ${className} ${comparandClass}`}
      >
        {renderContent()}
      </div>
    )
  }

  if (config.favouritesEnabled) {
    return (
      <div className={`product ${className} ${comparandClass}`}>
        <Link
          onClick={handleGA}
          to={ourAffiliateLink ? outgoingLink : productPagelink}
          data-cy="product"
          target={ourAffiliateLink ? '_blank' : null}
          rel={ourAffiliateLink ? 'sponsored' : null}
        >
          <div className="image-link">
            <LazyLoadImage
              alt={shortTitle}
              image={image}
              useOriginal={SKIP_IMAGEKIT_STORES.includes(
                store.name.toLowerCase()
              )}
            />
          </div>
        </Link>

        <div key="footer" className="footer">
          {showStore && <h4 className="product-brand">{store.name}</h4>}
          <h4>{shortTitle}</h4>
          <ProductPrice {...{ prices, isDiscounted, isStocked }} />
        </div>

        {isClient && <FavouriteButtonIcon {...{ product }} />}
      </div>
    )
  }

  return (
    <Link
      className={`product ${className} ${comparandClass}`}
      onClick={handleGA}
      to={ourAffiliateLink ? outgoingLink : productPagelink}
      data-cy="product"
      target={ourAffiliateLink ? '_blank' : null}
      rel={ourAffiliateLink ? 'sponsored' : null}
    >
      <div className="image-link">
        <LazyLoadImage
          alt={shortTitle}
          image={image}
          useOriginal={SKIP_IMAGEKIT_STORES.includes(store.name.toLowerCase())}
        />
      </div>
      <div key="footer" className="footer">
        {showStore && <h4 className="product-brand">{store.name}</h4>}
        <h4>{shortTitle}</h4>
        <ProductPrice {...{ prices, isDiscounted, isStocked }} />
      </div>
    </Link>
  )
}

ProductCard.propTypes = {
  className: PropTypes.string,
  gaEventContext: PropTypes.string,
  handleOpen: PropTypes.func,
  match: PropTypes.object,
  ourAffiliateLink: PropTypes.any,
  pageName: PropTypes.any,
  position: PropTypes.number,
  showStore: PropTypes.bool,
  style: PropTypes.object,
  useCompareModal: PropTypes.bool,
  ...productPropTypes
}

ProductCard.displayName = 'ProductCard'

export default withRouter(ProductCard)
